import React, { useEffect } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import {
  getInvoiceDetails,
  downloadInvoice,
} from "store/actions/subscriptionAction"
import { showAlert } from "store/reducers/alertReducer"
import { clearInvoiceDetails } from "store/reducers/subscriptionReducer"
import { ROUTES } from "constants/routes"
import { downloadInvoicePdf } from "helpers/invoicePdf"
import "./billing.scss"

const statusColors = {
  paid: "success",
  pending: "warning",
  failed: "danger",
  cancelled: "secondary",
}

const InvoiceDetails = () => {
  const { t } = useTranslation()
  document.title = t("Invoice Details ")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const invoiceId = searchParams.get("id")

  const { invoicesLoading, invoiceDetails, actionLoading } = useSelector(
    state => state.Subscription
  )

  useEffect(() => {
    if (invoiceId) {
      dispatch(getInvoiceDetails({ id: invoiceId }))
    }
    return () => {
      dispatch(clearInvoiceDetails())
    }
  }, [dispatch, invoiceId])

  const invoicePdfLabels = {
    invoice: t("Invoice"),
    date: t("Date"),
    status: t("Status"),
    billTo: t("Billing Info"),
    company: t("Company"),
    email: t("Email"),
    address: t("Address"),
    description: t("Plan"),
    billingCycle: t("Billing Cycle"),
    period: t("Period"),
    amount: t("Amount"),
    total: t("Total Amount"),
    paymentGateway: t("Payment Gateway"),
    transactionId: t("Transaction ID"),
    thankYou: t("Thank you for your business."),
  }

  const handleDownload = () => {
    dispatch(downloadInvoice({ id: invoiceId })).then(action => {
      const { status, message, data } = action.payload || {}
      if (status && data?.downloadUrl) {
        window.open(data.downloadUrl, "_blank")
        return
      }
      const invoiceData = data?.invoice || inv
      if (status && invoiceData) {
        try {
          downloadInvoicePdf(invoiceData, {
            issuerName: t("Wulooj"),
            labels: invoicePdfLabels,
          })
          dispatch(
            showAlert({
              text: t("Invoice PDF downloaded successfully"),
              type: "success",
            })
          )
        } catch {
          dispatch(
            showAlert({
              text: t("Download failed."),
              type: "error",
            })
          )
        }
        return
      }
      dispatch(
        showAlert({
          text: message || t("Download failed."),
          type: "error",
        })
      )
    })
  }

  const formatDate = dateStr => {
    if (!dateStr) return "-"
    return new Date(dateStr).toLocaleDateString()
  }

  const inv = invoiceDetails

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <BackButton />
              <h4 className="mb-0">{t("Invoice Details")}</h4>
            </div>
            {inv?._id && (
              <Button
                color="primary"
                outline
                onClick={handleDownload}
                disabled={actionLoading}
              >
                <i className="bx bx-download me-1" />
                {t("Download PDF")}
              </Button>
            )}
          </div>

          {invoicesLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : !inv?._id ? (
            <div className="text-center py-5 text-muted">
              {t("Invoice not found.")}
            </div>
          ) : (
            <Row>
              <Col lg={8}>
                <Card>
                  <CardBody>
                    <Row className="mb-4">
                      <Col sm={6}>
                        <h5 className="fw-bold mb-1">
                          {t("Invoice")}{" "}
                          <span className="text-primary">
                            #{inv.invoiceNumber || inv._id}
                          </span>
                        </h5>
                        <p className="text-muted mb-0">
                          {t("Date")}: {formatDate(inv.createdAt || inv.date)}
                        </p>
                      </Col>
                      <Col sm={6} className="text-sm-end">
                        <Badge
                          color={
                            statusColors[inv.status?.toLowerCase()] ||
                            "secondary"
                          }
                          className="fs-6 px-3 py-2"
                        >
                          {inv.status || "-"}
                        </Badge>
                      </Col>
                    </Row>

                    <Table borderless className="mb-0">
                      <tbody>
                        <tr>
                          <th className="text-muted fw-normal" width="40%">
                            {t("Plan")}
                          </th>
                          <td className="fw-semibold">
                            {inv.planName || inv.plan?.name || "-"}
                          </td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Billing Cycle")}
                          </th>
                          <td>
                            {inv.billingCycle
                              ? t(
                                  inv.billingCycle.charAt(0).toUpperCase() +
                                    inv.billingCycle.slice(1)
                                )
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Period Start")}
                          </th>
                          <td>{formatDate(inv.periodStart)}</td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Period End")}
                          </th>
                          <td>{formatDate(inv.periodEnd)}</td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Payment Gateway")}
                          </th>
                          <td>{inv.paymentGateway || "-"}</td>
                        </tr>
                        {inv.transactionId && (
                          <tr>
                            <th className="text-muted fw-normal">
                              {t("Transaction ID")}
                            </th>
                            <td className="font-monospace">
                              {inv.transactionId}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>

                    <hr />

                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">{t("Total Amount")}</h5>
                      <h4 className="mb-0 text-primary fw-bold">
                        {inv.currency || "SAR"}{" "}
                        {Number(inv.amount || 0).toFixed(2)}
                      </h4>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={4}>
                <Card>
                  <CardBody>
                    <h6 className="text-muted mb-3">{t("Billing Info")}</h6>
                    <p className="mb-1">
                      <strong>{t("Company")}:</strong>{" "}
                      {inv.billingInfo?.company || inv.tenantName || "-"}
                    </p>
                    <p className="mb-1">
                      <strong>{t("Email")}:</strong>{" "}
                      {inv.billingInfo?.email || "-"}
                    </p>
                    {inv.billingInfo?.address && (
                      <p className="mb-0">
                        <strong>{t("Address")}:</strong>{" "}
                        {inv.billingInfo.address}
                      </p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default InvoiceDetails
